import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { fetchData } from "../../api/client";
import { usePageContext } from "../../context/PageContext";
import AddClientForm from "../../components/AddClientForm";
import ClientFormPageChrome from "../../components/clients/ClientFormPageChrome";
import ClientFormActionsBar from "../../components/clients/ClientFormActionsBar";
import DuplicateClientDialog from "../../components/clients/DuplicateClientDialog";
import { getSelectedCompanyIds } from "../../utils/workspaceStorage";
import { clientFormPageShellSx, clientFormScrollSx } from "./clientFormPageLayout";

export default function ClientNewPage() {
  const navigate = useNavigate();
  const { currentPage } = usePageContext();
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveState, setSaveState] = useState({
    disableSave: true,
    isSubmitting: false,
    isDirty: true,
    hint: "",
  });
  const [discardOpen, setDiscardOpen] = useState(false);
  const [dupDialog, setDupDialog] = useState({
    open: false,
    duplicates: [],
    attemptedPayload: null,
  });

  const handleSaveStateChange = useCallback((next) => {
    setSaveState((prev) => ({ ...prev, ...next }));
  }, []);

  const defaultProjectId = useMemo(() => {
    const ids = getSelectedCompanyIds();
    return ids.length ? ids[0] : null;
  }, []);

  const navigateBack = () => {
    navigate(`/clients?page=${currentPage}`);
  };

  const handleCancel = () => {
    if (saveState.isDirty) {
      setDiscardOpen(true);
      return;
    }
    navigateBack();
  };

  const handleSubmit = async (payload) => {
    try {
      setSaving(true);
      setSaveError(null);
      const resp = await fetchData("/clients", "POST", payload);
      const created = resp?.data || resp;
      const newId = created?.id;
      if (!newId) {
        throw new Error("Cliente criado, mas a API não retornou o ID.");
      }
      navigate(`/clients/${newId}?created=1`, { replace: true });
    } catch (e) {
      if (e?.code === "errors.clients.duplicate") {
        setDupDialog({
          open: true,
          duplicates: e?.data?.duplicates || [],
          attemptedPayload: payload || null,
        });
        return;
      }
      setSaveError(e?.message || "Erro ao salvar o cliente.");
      throw e;
    } finally {
      setSaving(false);
    }
  };

  const formBusy = saving || saveState.isSubmitting;

  const statusText = useMemo(() => {
    if (formBusy) return null;
    if (saveState.validationErrorCount > 0) {
      return `${saveState.validationErrorCount} pendência${saveState.validationErrorCount > 1 ? "s" : ""} no formulário`;
    }
    return "Novo cadastro";
  }, [formBusy, saveState.validationErrorCount]);

  return (
    <Box sx={clientFormPageShellSx}>
      <ClientFormPageChrome mode="create" listPage={currentPage} onCancel={handleCancel} />

      {saveError ? (
        <Alert severity="error" sx={{ mb: 1.5, flexShrink: 0 }} onClose={() => setSaveError(null)}>
          {saveError}
        </Alert>
      ) : null}

      <Box sx={clientFormScrollSx}>
        <AddClientForm
          client={{}}
          defaultProjectId={defaultProjectId}
          isFetching={saving}
          onClose={handleCancel}
          onSubmit={handleSubmit}
          variant="page"
          formId="client-new-form"
          hideFooterActions
          onSaveStateChange={handleSaveStateChange}
          onDuplicateDetected={({ duplicates, attemptedPayload }) => {
            setDupDialog({
              open: true,
              duplicates,
              attemptedPayload: attemptedPayload || null,
            });
          }}
        />
      </Box>

      <ClientFormActionsBar
        statusText={statusText}
        hintText={formBusy ? null : saveState.hint}
        isBusy={formBusy}
      >
        <Button variant="outlined" onClick={handleCancel} disabled={formBusy}>
          Cancelar
        </Button>
        <Button
          type="submit"
          form="client-new-form"
          variant="contained"
          disabled={saveState.disableSave || formBusy}
        >
          {formBusy ? <CircularProgress size={20} /> : "Criar cliente"}
        </Button>
      </ClientFormActionsBar>

      <Dialog open={discardOpen} onClose={() => setDiscardOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Descartar cadastro?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Os dados preenchidos serão perdidos se você sair agora.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDiscardOpen(false)}>Continuar preenchendo</Button>
          <Button color="error" variant="contained" onClick={navigateBack}>
            Descartar
          </Button>
        </DialogActions>
      </Dialog>

      <DuplicateClientDialog
        open={dupDialog.open}
        duplicates={dupDialog.duplicates}
        attemptedPayload={dupDialog.attemptedPayload}
        secondaryLabel="novo cadastro"
        onChangePhone={() => {
          setDupDialog((s) => ({ ...(s || {}), open: false }));
          setSaveError("Altere o telefone ou outro dado em conflito para continuar.");
        }}
        onClose={() => setDupDialog((s) => ({ ...(s || {}), open: false }))}
        onMerged={({ primaryId }) => {
          setDupDialog((s) => ({ ...(s || {}), open: false }));
          if (primaryId) navigate(`/clients/${primaryId}?merged=1`, { replace: true });
        }}
      />
    </Box>
  );
}
